import type { SignalKind } from '../../../shared/enums';
import { h } from './dom';

/** Line icons for the desktop chrome (D65), drawn from our own static path data — never user text —
 *  so building them with createElementNS stays as safe as h(). 24×24, stroked with currentColor. */

const SVG_NS = 'http://www.w3.org/2000/svg';

const PATHS = {
  overview: ['M3 3h7v9H3z', 'M14 3h7v5h-7z', 'M14 12h7v9h-7z', 'M3 16h7v5H3z'],
  brief: ['M6 3h9l4 4v14H6z', 'M15 3v4h4', 'M9 12h7', 'M9 16h7'],
  activity: ['M3 12h4l3 7 4-16 3 9h4'],
  today: ['M4 5h16v16H4z', 'M4 10h16', 'M8 3v4', 'M16 3v4'],
  signals: ['M12 3l2.4 5.6 6.1.6-4.6 4 1.4 6-5.3-3.2-5.3 3.2 1.4-6-4.6-4 6.1-.6z'],
  pipeline: ['M4 4h4v16H4z', 'M10 4h4v11h-4z', 'M16 4h4v7h-4z'],
  route: ['M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z', 'M9 4v14', 'M15 6v14'],
  emails: ['M3 5h18v14H3z', 'm3 6 9 7 9-7'],
  reports: ['M4 20V11', 'M10 20V4', 'M16 20v-6', 'M3 20h18'],
  watchlist: ['M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
  search: ['M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z', 'm20 20-4.2-4.2'],
  kind_food_drink: [
    'M5 8h11v5a5 5 0 0 1-5 5h-1a5 5 0 0 1-5-5z',
    'M16 9h1.5a2.5 2.5 0 0 1 0 5H16',
    'M8 3v2',
    'M12 3v2',
  ],
  kind_retail: ['M5 8h14l-1 12H6z', 'M9 8V6a3 3 0 0 1 6 0v2'],
  kind_food_truck: [
    'M3 7h11v9H3z',
    'M14 10h4l3 3v3h-7z',
    'M5 18a2 2 0 1 0 4 0 2 2 0 0 0-4 0z',
    'M15 18a2 2 0 1 0 4 0 2 2 0 0 0-4 0z',
  ],
  kind_service: [
    'M8.5 6.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z',
    'M8.5 17.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z',
    'm8 8 12 10',
    'M8 16 20 6',
  ],
} as const;

export type IconName = keyof typeof PATHS;

export const icon = (name: IconName): SVGSVGElement => {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', 'icon');
  for (const d of PATHS[name]) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
  }
  return svg;
};

/** What kind of place a new business is, as a tinted tile (cup, bag, truck, scissors). */
export const kindIcon = (kind: SignalKind): HTMLElement =>
  h('span', { class: `kindicon kindicon--${kind}` }, icon(`kind_${kind}`));
